import { Box, Button, Tooltip, Stack, Modal } from "@mui/material";
import { goExternal } from "../utils/shoplineEmbedded";

import {
  useLazyGetProductQuery,
  useLazyGetAppsumoProductQuery,
  useGetAddOnQuery,
} from "../redux/api/productApi";
import {
  FormEvent,
  useEffect,
  useState,
  useContext,
  useMemo,
  ChangeEvent,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { Routes, Route, useLocation } from "react-router-dom";
import { IPlanDetail } from "../redux/api/types";

import "./pricing.page.css"; // ← all styles live here
import { useAppDispatch, useAppSelector } from "../redux/store";
import { setModule } from "../redux/features/genieSlice";
import { setPayment } from "../redux/features/paymentSlice";
import { useLazyCreateCheckoutSessionQuery } from "../redux/api/subscriptionApi";
import { getDesignTokens, ColorModeContext } from "../theme";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { PaletteMode } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import logo_white from "../assets/logo_white.png";
import { isMobile } from "react-device-detect";
import img54 from "../assets/newimg/logo_ocesha.png";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { usePlanDetailsMutation } from "../redux/api/productApi";
import { LoadingButton } from "@mui/lab";
import InfoIcon from "@mui/icons-material/Info";
import OceshaLogo from "../assets/ocesha_images/ocesha_logo.png";
import { logout } from "redux/features/userSlice";
import { toast } from "react-toastify";
import { setCookie, getCookie, removeCookie } from "../utils/storageUtils";
import ocesha_logo from "../assets/ocesha_images/ocesha_logo.png";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";

// Define interface for referral event
interface ReferralEvent {
  detail: {
    referralId: string;
  };
}

const return_domain = process.env.REACT_APP_RETURN_DOMAIN_NAME || "";

// ─── Static feature lists matching screenshot ───────────────────────────────
/* const STARTER_FEATURES = [
  "AI-powered content recommendations",
  "Generate 2 blogs/podcasts per month",
  "Create up to 2 interactive AI-powered courses",
  "Authority (profile) page for discoverability",
  "Basic analytics and engagement tracking",
  "Email support",
]; */
const STARTER_FEATURES = [
  "Publish & sell courses",
  "Accept payments (Stripe)",
  "AI teaches your course",
  "Student Q&A (any language)",
  "Certificates",
  "Sales page & checkout",
  "Social posts, blogs & images (AI)",
  "2 social channels",
  "Content scheduling",
  "Basic analytics"
];

/* const CREATOR_FEATURES = [
  "AI-powered content recommendations",
  "Generate blogs, social posts, and clips",
  "Publish to LinkedIn, X, Facebook, YouTube",
  "Authority (profile) page for discoverability",
  "Basic analytics and engagement tracking",
  "Email support",
]; */
const CREATOR_FEATURES = [
  "All marketing channels </br>LinkedIn, Facebook, X, YouTube & more",
  "AI Video Clips (45–60 seconds)",
  "Automatically find the best moments from your videos and podcasts and create 45–60 second clips.",
  "Advanced customization",
  "Advanced analytics",
  "Better discoverability (AI & SEO)",
  "Priority support"
];

/* const PRO_FEATURES = [
  "Everything in Creator",
  "Create and sell interactive AI-powered courses",
  "Digital Twin that teaches your content",
  "Monetize courses with your Stripe account",
  "Advanced analytics and performance insights",
  "Priority processing & faster content generation",
  "Lead generation on your authority page",
  "Lead capture on blogs and content",
  "Priority support",
]; */
const PRO_FEATURES = [
  "Create & sell AI-powered courses",
  "AI teaches in English and answers in any language",
  "Monetize courses",
  "Grow authority & discoverability with AI engines",
  "Advanced lead capture",
  "Priority support",
];

// ─── FREE PLAN REMOVED (commented out) ───
/* const FREE_FEATURES = [
  "AI course creation",
  "Course preview",
  "Basic customization",
  "Quizes & Exercises",
  "Student Q&A (limited)",
  "Authority page (basic)"
]; */

// ─── Heading shown above each plan's checklist (e.g. "Everything in Free, plus:") ───
const FEATURES_HEADING_BY_PLAN: Record<string, string> = {
  // "FREE": "Free includes:",   // FREE PLAN REMOVED
  CREATOR: "Everything in Free, plus:",
  PRO: "Everything in Creator, plus:",
  SCALE: "Everything in Pro, plus:",
};

// ─── Plan metadata keyed by plan_name (not array index) ─────────────────────
// This makes the UI resilient to plan ordering and to new plans being added
// in the DB — as long as the plan_name matches one of these keys, it will
// render with the right features, CTA color, and description.
const FEATURES_BY_PLAN: Record<string, string[]> = {
  // "FREE": FREE_FEATURES,   // FREE PLAN REMOVED
  "CREATOR": STARTER_FEATURES,
  "PRO": CREATOR_FEATURES,
  "SCALE": PRO_FEATURES,
};

const CTA_VARIANT_BY_PLAN: Record<string, string> = {
  // "FREE": "plan-card__cta--free",   // FREE PLAN REMOVED
  CREATOR: "plan-card__cta--starter",
  PRO: "plan-card__cta--creator",
  SCALE: "plan-card__cta--pro",
};

// ─── Canonical plan ordering (low → high tier) — used both to sort the
// cards left-to-right and to work out whether a given plan sits above or
// below the user's current plan (for the "Switch to" / "Upgrade to" CTA
// wording on the upgrade page). ───
// const PLAN_ORDER = ["FREE", "CREATOR", "PRO", "SCALE"];   // FREE PLAN REMOVED
const PLAN_ORDER = ["CREATOR", "PRO", "SCALE"];

/* const DESC_BY_PLAN: Record<string, string> = {
  STARTER: "A lightweight way to try content creation and courses.",
  CREATOR: "Perfect for building your authority and growing your audience.",
  PRO: "Everything in Creator, plus advanced tools to monetize and scale.",
}; */
const DESC_BY_PLAN: Record<string, string> = {
  // "FREE": "Experiecne OceSha",   // FREE PLAN REMOVED
  CREATOR: "Create more. Stay consistent.",
  PRO: "Grow your brand and audience.",
  SCALE: "Scale with courses & leads.",
};

// ─── Highlighted callout items shown above the checklist ───
type PlanCallout = {
  icon: JSX.Element;
  title: string;
  desc: string;
};

const CALLOUTS_BY_PLAN: Record<string, PlanCallout[]> = {
  // ─── FREE PLAN REMOVED (commented out) ───
  /* "FREE": [
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M4 8v8a1 1 0 001 1h10a1 1 0 001-1V8"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2.5 5a1 1 0 011-1h13a1 1 0 011 1v2a1 1 0 01-1 1h-13a1 1 0 01-1-1V5z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path
            d="M10 4v13"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      ),
      title: "Create 1 course",
      desc: "Build your first course with AI and explore everything OceSha can do.",
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect
            x="4"
            y="9"
            width="12"
            height="8"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <path
            d="M6.5 9V6.5a3.5 3.5 0 017 0V9"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      ),
      title: "Not for publishing",
      desc: "Courses created on the Free plan cannot be published or sold.",
    },
  ], */
  "CREATOR": [
    {
      icon: <SchoolRoundedIcon sx={{ fontSize: 20 }} />,
      title: "Up to 5 published courses",
      desc: "Build and sell up to 5 courses.",
    },
    {
      icon: <GroupsRoundedIcon sx={{ fontSize: 20 }} />,
      title: "Up to 500 active learns / month",
      desc: "Teach and engage your audience.",
    },
  ],
   "PRO": [
    {
      icon: <SchoolRoundedIcon sx={{ fontSize: 20 }} />,
      title: "Up to 20 published courses",
      desc: "Expand your expertise.",
    },
    {
      icon: <GroupsRoundedIcon sx={{ fontSize: 20 }} />,
      title: "Up to 2,500 active learns / month",
      desc: "Reach and teach more students.",
    },
  ],
  "SCALE": [
    {
      icon: <SchoolRoundedIcon sx={{ fontSize: 20 }} />,
      title: "Up to 50 published courses",
      desc: "Build large course library.",
    },
    {
      icon: <GroupsRoundedIcon sx={{ fontSize: 20 }} />,
      title: "Up to 10,000 active learns / month",
      desc: "Scale your impact.",
    },
  ],
};


// ─── Plan icon shown in the circular badge at the top of each card ──────────
const ICON_SVG_BY_PLAN: Record<string, JSX.Element> = {
  // ─── FREE PLAN REMOVED (commented out) ───
  /* "FREE": (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
      <path
        d="M23 3L3 11.5l7.5 3L14 22l3-6.5L23 3z"
        fill="currentColor"
        fillOpacity="0.16"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M10.5 14.5L23 3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  ), */
  CREATOR: (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
      <path
        d="M13 3c3 2.2 5 6 5 10.5 0 2-.4 3.6-1 5l-4 4-4-4c-.6-1.4-1-3-1-5C8 9 10 5.2 13 3z"
        fill="currentColor"
        fillOpacity="0.16"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="13" cy="11" r="2" fill="currentColor" />
      <path
        d="M9.5 18.5L7 23M16.5 18.5L19 23"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  ),
  PRO: (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
      <path
        d="M4 20h18"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M5 16l5-5 4 3 7-8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 6h4v4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  SCALE: (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
      <path
        d="M4 9l4.5 3L13 5l4.5 7L22 9l-2 11H6L4 9z"
        fill="currentColor"
        fillOpacity="0.16"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

// "Perfect for:" copy is no longer rendered on the card (not part of the
// reference design), kept here in case it's needed again later.
// const PERFECT_FOR_PLAN: Record<string, string> = {
//   "FREE": "Anyone curious to try AI-powered course creation before committing to a paid plan.",
//   STARTER: "Professionals who want to validate an idea, build authority, and experience AI-powered course creation before scaling.",
//   GROWTH: "Consultants, coaches, speakers, authors, educators, trainers, podcast hosts, and content creators focused on growing their audience and authority.",
//   PRO: "Experts, consultants, organizations, coaches, training companies, and teams ready to build a scalable AI-powered course business that generates revenue around the clock.",
// };

const ICON_COLOR_BY_PLAN: Record<string, string> = {
  // "FREE": "#4BA5EB",   // FREE PLAN REMOVED
  CREATOR: "#4BA5EB",
  PRO: "#7c3aed",
  SCALE: "#16a34a",
};

// ─── ACADEMY CARD (hard-coded, always shown as the last card) ───────────────
const ACADEMY_URL = "https://ocesha.com/academies";
const ACADEMY_COLOR = "#f59e0b";
const ACADEMY_FEATURES = [
  "Courses and multi-course programs",
  "AI-assisted content creation",
  "Assessments and certificates",
  "Free and paid learning",
  "Launch and implementation",
  "Ongoing management available",
];

const expired = localStorage.getItem("expired");
const hasValidExpiry = expired === "false"; //returns true if not expired

// ─── Trust badges ────────────────────────────────────────────────────────────
const TRUST_BADGES = [
  {
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path
          d="M18 4L6 9v8c0 7 5 13 12 15 7-2 12-8 12-15V9L18 4z"
          fill="#22c55e"
          fillOpacity="0.12"
          stroke="#22c55e"
          strokeWidth="1.6"
        />
        <path
          d="M13 18l3 3 7-7"
          stroke="#22c55e"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Safe & Secure",
    desc: "Your data is protected with enterprise-grade security and encryption.",
  },
  {
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <circle cx="18" cy="18" r="14" fill="#22c55e" fillOpacity="0.12" />
        <text
          x="18"
          y="22"
          textAnchor="middle"
          fontSize="14"
          fontWeight="600"
          fill="#22c55e"
          fontFamily="Sora, sans-serif"
        >
          $
        </text>
      </svg>
    ),
    title: "You Keep 100%",
    desc: "You connect your Stripe account and receive 100% of your earnings. We never take a cut.",
  },
  {
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <circle
          cx="18"
          cy="18"
          r="14"
          fill="#22c55e"
          fillOpacity="0.12"
          stroke="#22c55e"
          strokeWidth="1.5"
        />
        <path
          d="M10 20v-2a8 8 0 0116 0v2"
          stroke="#22c55e"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <rect x="9" y="20" width="3" height="5" rx="1" fill="#22c55e" />
        <rect x="24" y="20" width="3" height="5" rx="1" fill="#22c55e" />
      </svg>
    ),
    title: "We're Here to Help",
    desc: "Get priority support whenever you need help growing your business.",
  },
];

// ─── Bottom feature strip ────────────────────────────────────────────────────
const BOTTOM_FEATURES = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="8" fill="#4BA5EB" fillOpacity="0.15" />
        <path
          d="M8 14l4 4 8-8"
          stroke="#4BA5EB"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Create Effortlessly",
    desc: "Turn your ideas, podcasts, and knowledge into high-quality content in minutes.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="8" fill="#4BA5EB" fillOpacity="0.15" />
        <circle cx="14" cy="14" r="7" stroke="#4BA5EB" strokeWidth="1.5" />
        <path
          d="M14 10v4l3 3"
          stroke="#4BA5EB"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    title: "Grow Your Authority",
    desc: "Show up everywhere—search engines, AI platforms, and social feeds—to build your brand.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="8" fill="#4BA5EB" fillOpacity="0.15" />
        <path
          d="M8 18c2-4 8-4 12-2"
          stroke="#4BA5EB"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="14" cy="11" r="4" stroke="#4BA5EB" strokeWidth="1.5" />
      </svg>
    ),
    title: "Generate Leads",
    desc: "Business learns from your profile page and blogs to grow your audience and business.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="8" fill="#4BA5EB" fillOpacity="0.15" />
        <rect
          x="8"
          y="10"
          width="12"
          height="9"
          rx="2"
          stroke="#4BA5EB"
          strokeWidth="1.5"
        />
        <path
          d="M11 10V8h6v2"
          stroke="#4BA5EB"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    title: "Monetize Your Knowledge",
    desc: "Create and sell courses, and get paid directly through your Stripe account.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="8" fill="#4BA5EB" fillOpacity="0.15" />
        <path
          d="M8 20l4-8 4 6 3-4 3 6"
          stroke="#4BA5EB"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Scale Your Impact",
    desc: "Save time, reach more people, and build a business that grows with you.",
  },
];

// ─── Skeleton placeholder for a single plan card, shown while plans are loading ───
const PlanCardSkeleton = () => (
  <div className="plan-card plan-card--skeleton" aria-hidden="true">
    <div className="skeleton-icon skeleton-shimmer-bg" />
    <div className="skeleton-shimmer-bg skeleton-title" />
    <div className="skeleton-shimmer-bg skeleton-desc" />
    <div className="skeleton-shimmer-bg skeleton-price" />
    <div className="skeleton-shimmer-bg skeleton-cta" />

    <div className="plan-card__divider" />

    <div className="skeleton-shimmer-bg skeleton-features-heading" />
    <ul className="plan-card__features">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i}>
          <div className="skeleton-shimmer-bg skeleton-feature-icon" />
          <div
            className={`skeleton-shimmer-bg skeleton-feature-text ${
              i === 3 ? "skeleton-feature-text--short" : ""
            }`}
          />
        </li>
      ))}
    </ul>
  </div>
);

// ─── FREE PLAN REMOVED: helper used everywhere plans are filtered ───
const isFreePlanName = (name?: string) => (name || "").toUpperCase() === "FREE";

// ─────────────────────────────────────────────────────────────────────────────
const PricingPage = () => {
  const isAuthenticated = localStorage.getItem("logged_in");
  const [mode, setMode] = useState<PaletteMode>("dark");
  const userRole = localStorage.getItem("userRole");
  const [couponCode, setCouponCode] = useState<string>("");

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCouponCode(event.target.value);
  };

  const colorMode1 = useMemo(
    () => ({
      toggleColorMode: () => {
        userRole === "admin" ? setMode(() => "light") : setMode(() => "dark");
      },
    }),
    [],
  );

  const theme1 = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  const [getProduct, getState] = useLazyGetProductQuery();
  const [getAppsumoProduct, getAppsumoState] = useLazyGetAppsumoProductQuery();
  const [createCheckoutSession, checkoutState] =
    useLazyCreateCheckoutSessionQuery();
  const [period, getPeriodState] = useState("monthly");
  const [productModule, setProductModule] = useState("");
  const [additionalContract, setAdditionalContract] = useState(0);
  const [addon, setAddOn] = useState({
    plan_name: "",
    price: 0,
    price_id: "",
    product_id: "",
  });

  const emptyPlan: IPlanDetail = {
    user: 1,
    plan_name: "",
    total_wishes: 0,
    price: "",
    period: "",
    product_id: "",
    price_id: "",
    subscription_level: "",
    feature: [],
    contracts: 0,
    type: "",
    button_text: "",
    save: "",
  };

  const [plans, setPlans] = useState<IPlanDetail[]>([emptyPlan]);
  const [appsumoplans, setAppsumoPlans] = useState<IPlanDetail[]>([emptyPlan]);
  const [referralInfo, setReferralInfo] = useState<ReferralEvent | null>(null);
  const [showRemaining, setShowRemaining] = useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.userState.user);
  const genieSelector = useAppSelector((state) => state.genieState);

  const [planDetails, setPlanDetails] = useState<any>();
  const [getuserPlanDetails] = usePlanDetailsMutation();

  const location = useLocation();
  const { campIdFromUpgrade } = location.state || {};
  const { CreatingfromLanding } = location.state || {};
  const { UrlfromLanding } = location.state || {};
  const { uploadedvideo } = location.state || {};
  const { uploadedVideoName } = location.state || {};
  const { useremail } = location.state || {};
  const { fromNonSubscribed } = location.state || {};
  const { from_page } = location.state || {};
  const [helpervideosrc, setHelperVideoSrc] = useState<any>();
  const [helperPopup, setHelperPopup] = useState<boolean>(false);
  const [activeItem, setActiveItem] = useState(
    localStorage.getItem("activeItem") || "dashboard",
  );
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pageTopRef = useRef<HTMLDivElement>(null);
  const FromSignUp = location.state?.FromSignUp || "";
  const email = useremail ? useremail : localStorage.getItem("user_email");

  // ── Scroll to top on mount (e.g. when navigating from registration) ──────
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    // Also scroll the nearest scrollable ancestor in case the app uses an inner scroll container
    pageTopRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
  }, []);

  // ── Fetch current user plan ──────────────────────────────────────────────
  const fetchPlanDatas = async () => {
    try {
      const response = await getuserPlanDetails({
        email: email || "",
        campaignid: "",
      });
      if ("data" in response) setPlanDetails(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchPlanDatas();
    window.scrollTo(0, 0);
  }, []);

  // Auto-select a plan when period, plans, or the user's current plan change.
  // - Upgrade page: highlight the plan the user is currently subscribed to
  //   (matched by plan_name, falling back to subscription_level) sourced
  //   from /products/get-plan-details (planDetails) or localStorage.
  // - Signup/trial page: default to the Growth plan.
  useEffect(() => {
    const isFromUpgrade = localStorage.getItem("fromUpgrade") === "true";
    const filtered = plans
      .filter((p) => p.period === period)
      // FREE PLAN REMOVED: never offer the Free plan (previously only hidden on upgrade)
      // .filter(
      //   (p) => !(isFromUpgrade && (p.plan_name || "").toUpperCase() === "FREE"),
      // );
      .filter((p) => !isFreePlanName(p.plan_name));
    if (filtered.length === 0) return;

    if (isFromUpgrade) {
      const currentPlanName = (planDetails?.plan_name || "").toUpperCase();
      const currentSubscriptionLevel =
        planDetails?.subscription_level ||
        localStorage.getItem("subscription_level") ||
        "";

      const currentPlan =
        (currentPlanName &&
          filtered.find(
            (p) => (p.plan_name || "").toUpperCase() === currentPlanName,
          )) ||
        (currentSubscriptionLevel &&
          filtered.find(
            (p) => p.subscription_level === currentSubscriptionLevel,
          ));

      setSelectedPlanId(
        currentPlan ? currentPlan.price_id : filtered[0].price_id,
      );
    } else {
      const growthPlan = filtered.find(
        (p) => (p.plan_name || "").toUpperCase() === "GROWTH",
      );
      setSelectedPlanId(
        growthPlan ? growthPlan.price_id : filtered[0].price_id,
      );
    }
  }, [plans, period, planDetails]);

  // ── Identify the user's current plan, for the in-card "Current Plan"
  //    badge and the Switch/Upgrade CTA wording ──────────────────────────
  const currentPlanName = (planDetails?.plan_name || "").toUpperCase();
  const currentSubscriptionLevel =
    planDetails?.subscription_level ||
    localStorage.getItem("subscription_level") ||
    "";

  const isCurrentPlan = (plan: any) => {
    if (localStorage.getItem("fromUpgrade") !== "true") return false;
    if (currentSubscriptionLevel) {
      return plan.subscription_level === currentSubscriptionLevel;
    }
    const planName = (plan.plan_name || "").toUpperCase();
    return currentPlanName ? planName === currentPlanName : false;
  };

  const exactCurrentPlan = currentSubscriptionLevel
    ? plans.find((p: any) => p.subscription_level === currentSubscriptionLevel)
    : undefined;

  const currentTierName = (
    exactCurrentPlan?.plan_name || currentPlanName || ""
  ).toUpperCase();
  const currentPlanIndex = PLAN_ORDER.indexOf(currentTierName);

  // Rewardful referral listener
  useEffect(() => {
    const handleReferral = (event: CustomEvent<ReferralEvent>) =>
      setReferralInfo(event.detail);
    window.addEventListener(
      "rewardful.referral",
      handleReferral as EventListener,
    );
    return () =>
      window.removeEventListener(
        "rewardful.referral",
        handleReferral as EventListener,
      );
  }, []);

  // Fetch add-on
  useEffect(() => {
    fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}/api/products/get-add-on`)
      .then((r) => r.json())
      .then((data) => setAddOn(data.data));
  }, [setAddOn]);

  // Fetch products
  useEffect(() => {
    const value = {
      _id: "",
      product_name: "CodeGenie",
      product_module: `${process.env.REACT_APP_MAIN_PRODUCT_NAME}`,
      userid: "",
      productid: "",
    };
    getProduct(value);
  }, [genieSelector, getProduct]);

  useEffect(() => {
    const { data } = getState;
    if (data?.plan_details) {
      setPlans([...data.plan_details]);
      setProductModule(data.product_module);
    } else setPlans([emptyPlan]);
  }, [getState]);

  // Fetch AppSumo products
  useEffect(() => {
    const value = {
      _id: "",
      product_name: "CodeGenie",
      product_module: `${process.env.REACT_APP_MAIN_PRODUCT_NAME}`,
      userid: "",
      productid: "",
    };
    getAppsumoProduct(value);
  }, [genieSelector, getAppsumoProduct]);

  useEffect(() => {
    const { data } = getAppsumoState;
    if (data?.plan_details) {
      setAppsumoPlans([...data.plan_details]);
      setProductModule(data.product_module);
    } else setAppsumoPlans([emptyPlan]);
  }, [getAppsumoState]);

  // Checkout redirect
  useEffect(() => {
    if (checkoutState.isSuccess) {
      goExternal(checkoutState.data?.checkout_url);   // popup when inside SHOPLINE
      if (CreatingfromLanding === "true") {
        localStorage.setItem("CreatingfromLanding", CreatingfromLanding);
        if (UrlfromLanding)
          localStorage.setItem("UrlfromLanding", UrlfromLanding);
        else if (uploadedvideo && uploadedVideoName) {
          localStorage.setItem("uploadedvideo", uploadedvideo);
          localStorage.setItem("uploadedVideoName", uploadedVideoName);
        }
      }
    }
  }, [checkoutState, navigate]);

  // ── Form submit ──────────────────────────────────────────────────────────
  function handleSubmit(
    e: FormEvent<HTMLFormElement>,
    subscription_level: string,
    planName?: string,
  ) {
    localStorage.setItem("user_email", email);
    e.preventDefault();

    // ─── FREE PLAN REMOVED (commented out) ───
    /* const isFreePlan =
      subscription_level === "Free" ||
      (planName || "").toUpperCase() === "FREE";

    if (isFreePlan) {
      // Coming straight from signup + choosing Free Member: skip Stripe
      // entirely and land on onboarding, same shortcut used for partner
      // signups in signup.page.tsx.
      if (FromSignUp === "true") {
        localStorage.setItem("payment", "Success");
        localStorage.setItem("expired", "false");
        navigate("/codegenie/onboarding_page", {
          state: { FromSignUp: "true" },
        });
        return;
      }

      if (user?.email) navigate("/codegenie/smart_contracts");
      else navigate("/signup");
      return;
    } */

    if (isAuthenticated !== "true") {
      localStorage.setItem("fromPricing", "true");
      localStorage.setItem("subscription_level", subscription_level);
      let referral_param = "";
      if (referralInfo) referral_param = referralInfo.detail.referralId;
      localStorage.setItem("referral_param", referral_param);
      localStorage.setItem("couponcode", couponCode);
      navigate("/signup");
    } else {
      const emailVal = useremail
        ? useremail
        : localStorage.getItem("user_email");
      let referral_param = "";
      if (referralInfo) referral_param = referralInfo.detail.referralId;
      const fromUpgrade = localStorage.getItem("fromUpgrade");
      if (FromSignUp === "true") localStorage.setItem("FromSignUp", "true");
      createCheckoutSession({
        emailid: emailVal || "",
        username: emailVal || "",
        subscription_level,
        referral_param: referral_param || "",
        couponcode: couponCode,
        mode:
          fromUpgrade === "true" && fromNonSubscribed !== "true"
            ? "upgrade"
            : "",
        campaignid: campIdFromUpgrade ? campIdFromUpgrade : "",
        from_page: from_page || "pricing",
        return_domain,
      });
    }
  }

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    setAdditionalContract(Number(event.target.value));
  }

  const fromUpgrade = localStorage.getItem("fromUpgrade");

  // True on the initial fetch and on any refetch, so the skeleton shows
  // whenever we don't yet have real plan data to render.
  const isLoadingPlans = getState.isLoading || getState.isFetching;

  const filteredPlans = plans
    .filter((item) => item.period === period)
    // FREE PLAN REMOVED: always hide the Free plan (previously only hidden on upgrade)
    // .filter(
    //   (item) =>
    //     !(
    //       fromUpgrade === "true" &&
    //       (item.plan_name || "").toUpperCase() === "FREE"
    //     ),
    // )
    .filter((item) => !isFreePlanName(item.plan_name))
    .sort((a, b) => {
      const aIndex = PLAN_ORDER.indexOf((a.plan_name || "").toUpperCase());
      const bIndex = PLAN_ORDER.indexOf((b.plan_name || "").toUpperCase());
      // Unknown plan names (not in PLAN_ORDER) sort to the end rather than the start
      return (
        (aIndex === -1 ? PLAN_ORDER.length : aIndex) -
        (bIndex === -1 ? PLAN_ORDER.length : bIndex)
      );
    });

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* Invisible anchor at the very top — used by the scroll-to-top effect */}
      <div ref={pageTopRef} style={{ position: "absolute", top: 0 }} />

      {/* ── Academy card styles + 4-column grid ── */}
      <style>{`
        .plan-cards-grid {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }
        @media (max-width: 1200px) {
          .plan-cards-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 700px) {
          .plan-cards-grid { grid-template-columns: 1fr; }
        }
        .plan-card__cta--academy {
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          background: transparent;
          color: ${ACADEMY_COLOR};
          border: 1.5px solid ${ACADEMY_COLOR};
        }
        .plan-card__cta--academy:hover {
          background: rgba(245, 158, 11, 0.12);
          color: ${ACADEMY_COLOR};
          text-decoration: none;
        }
        .plan-card__price-custom {
          font-size: inherit;
        }
      `}</style>

      {/* ── NAVBAR (logged-out only) ── */}
      {localStorage.getItem("logged_in") !== "true" && (
        <div className="container">
          <div className="row">
            <nav className="navbar navbar-expand-md">
              <div className="container-fluid">
                <a className="navbar-brand" href={process.env.REACT_APP_LOGOUT_DOMAIN || "https://ocesha.ai"} style={{ width: "5%" }}>
                  <img
                    src={OceshaLogo}
                    className="img-fluid"
                    alt="Ocesha"
                    style={{ width: "95%" }}
                  />
                </a>
                <button
                  className="navbar-toggler"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarScroll"
                >
                  <span>
                    <i className="bi bi-list text-white" />
                  </span>
                </button>
                <div className="collapse navbar-collapse" id="navbarScroll">
                  <ul className="navbar-nav me-auto my-2 my-lg-0 navbar-nav-scroll ms-md-5 gap-4">
                    <li className="nav-item">
                      <a
                        className="nav-link nav_active"
                        style={{ color: "#4ba5eb", fontWeight: 800 }}
                        href={process.env.REACT_APP_LOGOUT_DOMAIN || "https://ocesha.ai"}
                      >
                        Home
                      </a>
                    </li>
                    <li
                      className="nav-item"
                      onClick={() => navigate("/contactus")}
                      style={{ cursor: "pointer" }}
                    >
                      <a className="nav-link nav_simple">Contact</a>
                    </li>
                    <li
                      className="nav-item"
                      onClick={() => navigate("/aboutus")}
                      style={{ cursor: "pointer" }}
                    >
                      <a className="nav-link nav_simple">About Us</a>
                    </li>
                  </ul>
                  <div className="d-flex gap-4" role="search">
                    <button
                      className="btn btn-outline-primary px-3"
                      onClick={() => navigate("/signin")}
                    >
                      Sign In
                    </button>
                    <button
                      className="button_leniar px-3"
                      type="submit"
                      onClick={() => navigate("/signup")}
                    >
                      Sign Up
                    </button>
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT ── */}
      <div style={{ width: "100%", overflowX: "hidden" }}>
        <div className="priceContainer">
          {/* HEADING */}
          <div className="price_head">
            {fromUpgrade ? (
              <>
                <h5 >{!hasValidExpiry ? "You subscription expired or payment failed" : ""}</h5>
                <h1 className="main-title">
                  {planDetails?.plan_name?.startsWith("Life Time")
                    ? "AppSumo Lifetime Deal"
                    : "Upgrade Your Plan"}
                </h1>
                <p className="main-subtitle">
                  Unlock more power, reach, and monetization tools to grow your brand,
                  <br />
                  generate leads, and build a thriving knowledge business.
                </p>
              </>
            ) : (
              <div className="trial-hero">
              </div>
            )}
            {(!planDetails?.plan_name?.startsWith("Life Time") && !fromUpgrade) && (
              <div className="yearly-badge">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ marginRight: 6 }}
                >
                  {/* box */}
                  <rect
                    x="2"
                    y="6"
                    width="12"
                    height="8"
                    rx="2"
                    fill="#4BA5EB"
                    fillOpacity="0.15"
                    stroke="#4BA5EB"
                    strokeWidth="1.2"
                  />

                  {/* lid */}
                  <path
                    d="M2 6h12"
                    stroke="#4BA5EB"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />

                  {/* ribbon vertical */}
                  <path
                    d="M8 6v8"
                    stroke="#4BA5EB"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />

                  {/* bow */}
                  <path
                    d="M8 6c-1.5 0-2.5-1-2.5-2s1-2 2.5 0c1.5-2 2.5-1 2.5 0s-1 2-2.5 2z"
                    stroke="#4BA5EB"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Get 2 months free when you subscribe yearly
              </div>
            )}
          </div>

          {/* BILLING TOGGLE */}
          {!planDetails?.plan_name?.startsWith("Life Time") && (
            <div className="billing-toggle">
              <button
                className={`toggle-btn ${period === "monthly" ? "active" : ""}`}
                onClick={() => getPeriodState("monthly")}
              >
                Pay Monthly
              </button>
              <button
                className={`toggle-btn ${period === "yearly" ? "active" : ""}`}
                onClick={() => getPeriodState("yearly")}
              >
                Pay Yearly&nbsp;
                {period === "yearly" ? (
                  <span className="save-badge">Save 16%</span>
                ) : (
                  <span className="save-badge-dim">Save 16%</span>
                )}
              </button>
            </div>
          )}

          {/* 14-DAYS TRIAL BADGE — shown below the Pay Monthly / Pay Yearly toggle (signup page only) */}
          {!fromUpgrade && (
            <>
              <style>{`
                @keyframes trialGlow {
                  0%, 100% { box-shadow: 0 0 18px rgba(75,165,235,0.35), 0 0 0 rgba(124,58,237,0); }
                  50%      { box-shadow: 0 0 32px rgba(75,165,235,0.6), 0 0 24px rgba(124,58,237,0.45); }
                }
                @keyframes trialShine {
                  0%   { background-position: 0% 50%; }
                  100% { background-position: 200% 50%; }
                }
                .trial-days-badge-wrap {
                  display: flex;
                  justify-content: center;
                  margin: 22px 0 8px;
                  padding: 0 16px;
                }
                .trial-days-badge {
                  display: inline-flex;
                  align-items: center;
                  gap: 12px;
                  padding: 12px 30px;
                  border-radius: 999px;
                  border: 1.5px solid rgba(75,165,235,0.55);
                  background: linear-gradient(135deg, rgba(75,165,235,0.14), rgba(124,58,237,0.14));
                  animation: trialGlow 2.6s ease-in-out infinite;
                }
                .trial-days-badge__text {
                  font-family: "Sora", "Poppins", sans-serif;
                  font-size: 34px;
                  font-weight: 800;
                  letter-spacing: 0.5px;
                  line-height: 1.1;
                  background: linear-gradient(90deg, #4BA5EB, #a78bfa, #22c55e, #4BA5EB);
                  background-size: 200% auto;
                  -webkit-background-clip: text;
                  background-clip: text;
                  -webkit-text-fill-color: transparent;
                  animation: trialShine 4s linear infinite;
                }
                .trial-days-badge__icon {
                  font-size: 30px;
                  line-height: 1;
                }
                @media (max-width: 600px) {
                  .trial-days-badge { padding: 10px 20px; gap: 8px; }
                  .trial-days-badge__text { font-size: 24px; }
                  .trial-days-badge__icon { font-size: 22px; }
                }
              `}</style>
              <div className="trial-days-badge-wrap">
                <div className="trial-days-badge">
                  {/* <span className="trial-days-badge__icon" aria-hidden="true">🎉</span> */}
                  <span className="trial-days-badge__text">14-Days Trial For All Plans</span>
                </div>
              </div>
            </>
          )}

          {/* GREEN INFO BAR */}
          {!fromUpgrade && (
            <div className="trial-info-bar-">
            </div>
          )}

          {/* PLAN CARDS */}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const selectedPlan = plans.find(
                (p) => p.price_id === selectedPlanId,
              );
              if (!selectedPlan) {
                alert("Please select a plan");
                return;
              };

              handleSubmit(
                event,
                selectedPlan.subscription_level,
                selectedPlan.plan_name,
              );
            }}
          >
            <div className="plan-cards-grid">
              {isLoadingPlans
                // FREE PLAN REMOVED: 3 plan cards + 1 Academy card = 4 skeletons
                ? Array.from({ length: 4 }).map((_, i) => (
                    <PlanCardSkeleton key={i} />
                  ))
                : filteredPlans.map((plan, index) => {
                const isSelected = selectedPlanId === plan.price_id;

                const planKey = (plan.plan_name || "").toUpperCase();
                const features = FEATURES_BY_PLAN[planKey] || [];
                const featuresHeading = FEATURES_HEADING_BY_PLAN[planKey] || "";
                const monthlyPrice = Number(plan.price).toLocaleString();
                const yearlyTotal = (Number(plan.price) * 10).toLocaleString();
                const ctaVariant =
                  CTA_VARIANT_BY_PLAN[planKey] ?? "plan-card__cta--creator";
                const planLabel = plan.plan_name || "Plan";
                const planDesc = DESC_BY_PLAN[planKey] || "";
                const iconColor = ICON_COLOR_BY_PLAN[planKey] ?? "#4BA5EB";
                const isCreator = planKey === "CREATOR";
                const isPro = planKey === "PRO";
                const isScale = planKey === "SCALE";
                // const isFree = planKey === "FREE";   // FREE PLAN REMOVED
                const isThisCurrentPlan = isCurrentPlan(plan);
                const thisPlanIndex = PLAN_ORDER.indexOf(planKey);
                const ctaLabel = fromUpgrade
                  ? isThisCurrentPlan
                    ? "Your Current Plan"
                    : currentPlanIndex !== -1 &&
                        thisPlanIndex !== -1 &&
                        thisPlanIndex < currentPlanIndex
                      ? `Switch to ${planLabel}`
                      : `Upgrade to ${planLabel}`
                  : isPro
                    ? "Start PRO Now"
                    // FREE PLAN REMOVED
                    // : isFree
                    //   ? "Get Started Free"
                    : "Start Now";

                return (
                  <div
                    key={plan.price_id || index}
                    className={`plan-card 
  ${isSelected ? "plan-card--selected" : ""} 
  ${!isSelected && isCreator ? "plan-card--creator" : ""}
  ${!isSelected && isPro ? "plan-card--pro" : ""}
  ${!isSelected && isScale ? "plan-card--scale" : ""}
`}
                    style={{ ["--plan-color" as any]: iconColor }}
                    onClick={() => setSelectedPlanId(plan.price_id)}
                  >
                    {/* ── "Current Plan" badge — shown only on the upgrade
                        page, on the card that matches the user's active
                        subscription ── */}
                    {isThisCurrentPlan && (
                      <div className="plan-card__current-badge">Current Plan</div>
                    )}

                    {/* ── Icon badge ── */}
                    <div
                      className="plan-card__icon"
                      style={{
                        background: `${iconColor}1F`,
                        color: iconColor,
                      }}
                    >
                      {ICON_SVG_BY_PLAN[planKey]}
                    </div>

                    {/* ── Name + description ── */}
                    <div
                      className="plan-card__name"
                      style={{ color: iconColor }}
                    >
                      {planLabel}
                    </div>
                    <div className="plan-card__desc">{planDesc}</div>

                    {/* ── Price ── */}
                    <div className="plan-card__price">
                      {period === "yearly" ? (
                        <>
                          <span className="price-main">${yearlyTotal}</span>
                          {/* FREE PLAN REMOVED: was {planLabel == "FREE" ? "forever" : "/ year"} */}
                          <span className="price-period">/ year</span>
                        </>
                      ) : (
                        <>
                          <span className="price-main">${monthlyPrice}</span>
                          {/* FREE PLAN REMOVED: was {planLabel == "FREE" ? "forever" : "/ month"} */}
                          <span className="price-period"> / month</span>
                        </>
                      )}
                    </div>

                    {/* ── CTA button — color varies by plan, sits above the feature list ── */}
                    <button
                      type="submit"
                      className={`plan-card__cta ${ctaVariant} ${isThisCurrentPlan ? "plan-card__cta--current" : ""}`}
                      disabled={isThisCurrentPlan}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isThisCurrentPlan) return;
                        setSelectedPlanId(plan.price_id);
                      }}
                    >
                      {ctaLabel}
                    </button>

                    {/* ── Highlighted callouts ── */}
                    {(CALLOUTS_BY_PLAN[planKey] || []).length > 0 && (
                      <div className="plan-card__callouts">
                        {CALLOUTS_BY_PLAN[planKey].map((callout, ci) => (
                          <div key={ci} className="plan-card__callout">
                            <div
                              className="plan-card__callout-icon"
                              style={{
                                background: `${iconColor}1F`,
                                color: iconColor,
                              }}
                            >
                              {callout.icon}
                            </div>
                            <div>
                              <div className="plan-card__callout-title">{callout.title}</div>
                              <div className="plan-card__callout-desc">{callout.desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── Features ── */}
                    {featuresHeading && (
                      <div className="plan-card__features-heading">
                        {featuresHeading}
                      </div>
                    )} 
                    <ul className="plan-card__features">
                      {features.map((feat, fi)   => {
                        return (
                          <li key={fi}>
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                            >
                              <circle
                                cx="9"
                                cy="9"
                                r="9"
                                fill={iconColor}
                                fillOpacity="0.18"
                              />
                              <path
                                d="M5.5 9l2.5 2.5 5-5"
                                stroke={iconColor}
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div
                              className="plan-card__feature-content"
                              dangerouslySetInnerHTML={{ __html: feat }}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}

              {/* ── ACADEMY CARD (hard-coded, always last) ──
                  Not a Stripe plan: no onClick on the card (never changes
                  selectedPlanId) and the CTA is a link, not a submit button,
                  so it can't trigger the form's checkout flow. */}
              {!isLoadingPlans && (
                <div
                  className="plan-card plan-card--academy"
                  style={{ ["--plan-color" as any]: ACADEMY_COLOR }}
                >
                  {/* ── Icon badge ── */}
                  <div
                    className="plan-card__icon"
                    style={{
                      background: `${ACADEMY_COLOR}1F`,
                      color: ACADEMY_COLOR,
                    }}
                  >
                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                      <path
                        d="M13 4L2 9.5 13 15l11-5.5L13 4z"
                        fill="currentColor"
                        fillOpacity="0.16"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6.5 12v5.5c0 1.5 3 3.5 6.5 3.5s6.5-2 6.5-3.5V12"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                      <path
                        d="M24 9.5V16"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  {/* ── Name + description ── */}
                  <div
                    className="plan-card__name"
                    style={{ color: ACADEMY_COLOR }}
                  >
                    Academy
                  </div>
                  <div className="plan-card__desc">
                    Your knowledge. Your brand. Your Academy.
                  </div>

                  {/* ── Price ── */}
                  <div className="plan-card__price">
                    <span className="price-main">Custom</span>
                  </div>

                  {/* ── CTA ── */}
                  <a
                    href={ACADEMY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="plan-card__cta plan-card__cta--academy"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Explore Academies
                  </a>

                  {/* ── Callout ── */}
                  <div className="plan-card__callouts">
                    <div className="plan-card__callout">
                      <div
                        className="plan-card__callout-icon"
                        style={{
                          background: `${ACADEMY_COLOR}1F`,
                          color: ACADEMY_COLOR,
                        }}
                      >
                        <SchoolRoundedIcon sx={{ fontSize: 20 }} />
                      </div>
                      <div>
                        <div className="plan-card__callout-title">
                          Your own branded academy
                        </div>
                        <div className="plan-card__callout-desc">
                          Launch a branded learning destination for your
                          organization, customers, employees, members, or
                          community.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Features ── */}
                  <div className="plan-card__features-heading">
                    Academy includes:
                  </div>
                  <ul className="plan-card__features">
                    {ACADEMY_FEATURES.map((feat, fi) => (
                      <li key={fi}>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                        >
                          <circle
                            cx="9"
                            cy="9"
                            r="9"
                            fill={ACADEMY_COLOR}
                            fillOpacity="0.18"
                          />
                          <path
                            d="M5.5 9l2.5 2.5 5-5"
                            stroke={ACADEMY_COLOR}
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="plan-card__feature-content">{feat}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </form>

          {/* FREE TRIAL REASSURANCE — signup only */}
          {!fromUpgrade && (
            <div className="trial-reassurance">
            </div>
          )}

          {/* TRUST BADGES */}
          <div className="trust-badges">
            {TRUST_BADGES.map((b, i) => (
              <div key={i} className="trust-badge">
                <div className="trust-badge__icon">{b.icon}</div>
                <div>
                  <div className="trust-badge__title">{b.title}</div>
                  <div className="trust-badge__desc">{b.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* BOTTOM STRIP */}
          <div className="bottom-strip">
            <div className="bottom-strip__heading">
              Do More. Earn More. Impact More.
            </div>
            <div className="bottom-features">
              {BOTTOM_FEATURES.map((f, i) => (
                <div key={i} className="bottom-feature">
                  <div className="bottom-feature__icon">{f.icon}</div>
                  <div className="bottom-feature__title">{f.title}</div>
                  <div className="bottom-feature__desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div >
      </div >
      <div className="bottom-footer-note">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          style={{ marginRight: 6 }}
        >
          <rect
            x="3"
            y="6"
            width="10"
            height="8"
            rx="2"
            stroke="#94A3B8"
            strokeWidth="1.3"
          />
          <path
            d="M5.5 6V4.5a2.5 2.5 0 015 0V6"
            stroke="#94A3B8"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
        You can upgrade, downgrade, or cancel your plan at any time.
      </div>
    </>
  );
};

export default PricingPage;
